import {useEffect, useRef, useCallback, useState} from 'react'
import {Banner, IconButton} from '@primer/react'
import {ChevronLeftIcon, ChevronRightIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import type {NudgeDefinition, NudgeVariant} from '../../types/nudge'
import styles from './NudgeCard.module.css'

interface NudgeCardProps {
  nudge: NudgeDefinition
  onDismiss: (nudge: NudgeDefinition) => void
  onDismissAll?: () => void
  onImpression: (nudge: NudgeDefinition) => void
  onClick: (nudge: NudgeDefinition, actionLabel: string) => void
  compact?: boolean
  onPrev?: () => void
  onNext?: () => void
  totalCount?: number
}

function toBannerVariant(_variant: NudgeVariant): 'info' | 'upsell' {
  return 'info'
}

export function NudgeCard({
  nudge,
  onDismiss,
  onDismissAll,
  onImpression,
  onClick,
  compact,
  onPrev,
  onNext,
  totalCount,
}: NudgeCardProps) {
  const lastImpressedNudgeIdRef = useRef<NudgeDefinition['id'] | null>(null)
  const [exiting, setExiting] = useState(false)

  useEffect(() => {
    if (lastImpressedNudgeIdRef.current !== nudge.id) {
      onImpression(nudge)
      lastImpressedNudgeIdRef.current = nudge.id
    }
  }, [nudge, onImpression])

  const handleDismiss = useCallback(() => {
    setExiting(true)
  }, [])

  const handleAnimationEnd = useCallback(
    (e: React.AnimationEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return
      if (exiting) {
        setExiting(false)
        if (onDismissAll) {
          onDismissAll()
        } else {
          onDismiss(nudge)
        }
      }
    },
    [exiting, nudge, onDismiss, onDismissAll],
  )

  const Icon = nudge.icon
  const [firstAction, secondAction] = nudge.actions ?? []
  const showCycleControls = typeof totalCount === 'number' && totalCount > 1 && onPrev && onNext
  const hasFooter = firstAction || showCycleControls

  return (
    <div className={clsx(styles.wrapper, exiting && styles.exiting)} onAnimationEnd={handleAnimationEnd}>
      <Banner
        className={clsx(hasFooter && styles.bannerWithFooter)}
        data-testid={`nudge-${nudge.id}`}
        variant={toBannerVariant(nudge.variant)}
        layout={compact ? 'compact' : 'default'}
        title={nudge.title}
        description={nudge.description}
        leadingVisual={Icon ? <Icon size={16} /> : undefined}
        onDismiss={handleDismiss}
      />
      {hasFooter && (
        <div className={styles.footer} data-variant={toBannerVariant(nudge.variant)}>
          <div className={styles.footerActions}>
            {firstAction && (
              <Banner.PrimaryAction
                as={firstAction.href ? 'a' : 'button'}
                href={firstAction.href}
                target={firstAction.external ? '_blank' : undefined}
                rel={firstAction.external ? 'noopener noreferrer' : undefined}
                onClick={() => {
                  firstAction.onClick?.()
                  onClick(nudge, firstAction.label)
                }}
              >
                {firstAction.label}
              </Banner.PrimaryAction>
            )}
            {secondAction && (
              <Banner.SecondaryAction
                as={secondAction.href ? 'a' : 'button'}
                href={secondAction.href}
                target={secondAction.external ? '_blank' : undefined}
                rel={secondAction.external ? 'noopener noreferrer' : undefined}
                onClick={() => {
                  secondAction.onClick?.()
                  onClick(nudge, secondAction.label)
                }}
              >
                {secondAction.label}
              </Banner.SecondaryAction>
            )}
          </div>
          {showCycleControls && (
            <div className={styles.cycleControls} role="group" aria-label="Cycle through tips">
              <IconButton
                icon={ChevronLeftIcon}
                size="small"
                variant="invisible"
                aria-label="Previous tip"
                onClick={onPrev}
              />
              <IconButton
                icon={ChevronRightIcon}
                size="small"
                variant="invisible"
                aria-label="Next tip"
                onClick={onNext}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
